import { useState, useEffect, useMemo, useCallback } from "react";
import { RefreshCw, User, Timer, Search } from "lucide-react";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Skeleton } from "../../../ui/skeleton";
import {
  getAllApps,
  getAllAppsCount,
  getAllAppsWithProfile,
  getAllAppsWithProfileSearch,
  getAppsForPublishers,
  getAppsForPublishersCount,
  getAppsForPublishersWithProfile,
  getPublishers,
  getPublishersWithProfiles,
  getTotalAppsCount,
  updateTrustLevel,
  updateTrustLevelForProfile,
} from "../Api/resourceApi";
import { myLocalStorage } from "../../../StorageHelper";
import { Device } from "../../interfaces/EndpointInterfaces";
import {
  ComputerUsersPublishersAppsMembership,
  Publishers,
  ResourceAPP,
} from "../resourceInterface";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { getInheritedEndpointResourceStatusValue, getTimerDisplay, labelMapping } from "../helper";
import useUserStore from "../../../../services/userStore";

export interface ApplicationControlTabProps {
  selectedUser: string | null;
  onUserSelect: (userId: string | null) => void;
  users: any[];
  device: Device;
  profileId: number;
  os: string;
  fromDetails: boolean | false;
}

export default function ApplicationControlTab({
  selectedUser,
  onUserSelect,
  users,
  device,
  profileId,
  os,
  fromDetails
}: ApplicationControlTabProps) {
  const PAGE_SIZE = 50;
  const [selectedPublisher, setSelectedPublisher] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedApp, setSelectedApp] = useState<ResourceAPP | null>(null);
  const [publisherSearch, setPublisherSearch] = useState("");
  const [appSearch, setAppSearch] = useState("");
  const [appSearchInput, setAppSearchInput] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [appStatuses, setAppStatuses] = useState<Record<string, Record<string, string>>>({});
  const [apps, setApps] = useState<ResourceAPP[]>([]);

  const [privilegeFilter, setPrivilegeFilter] = useState("ALL");
  const normalizedPrivilegeFilter = privilegeFilter === "ALL" ? "" : privilegeFilter;
  const { email, role } = useUserStore((state) => state.user);

  const [publishers, setPublishers] = useState<Publishers[]>([]);
  const [loadingPublishers, setLoadingPublishers] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [totalAppsCountDetails, setTotalAppsCountDetails] = useState(0);

  let getData = selectedUser !== "system" ? "computerUsersPublishersAppsMembership" : "defaultProfileAppsResource";

  const getAppPayloadKey = useCallback((app: ResourceAPP): string => {
    if (app?.[getData]) return getData;
    if (app?.computerUsersPublishersAppsMembership) return "computerUsersPublishersAppsMembership";
    if (app?.defaultProfileAppsResource) return "defaultProfileAppsResource";
    return getData;
  }, [getData]);

  const getAppData = useCallback((app: ResourceAPP | null | undefined): any => {
    if (!app) return null;
    const key = getAppPayloadKey(app);
    const nestedData = app?.[key];
    return nestedData ?? app;
  }, [getAppPayloadKey]);

  const getAppId = useCallback((app: ResourceAPP | null | undefined): number | string | undefined => {
    const appData = getAppData(app);
    return appData?.appId ?? appData?.id;
  }, [getAppData]);

  const getAppNameValue = useCallback((app: ResourceAPP | null | undefined): string => {
    const appData = getAppData(app);

    return String(appData?.appName ?? appData?.name ?? appData?.path ?? appData?.appPath ?? "");
  }, [getAppData]);

  const getAppPathValue = useCallback((app: ResourceAPP | null | undefined): string => {
    const appData = getAppData(app);
    return String(appData?.path ?? appData?.appPath ?? "");
  }, [getAppData]);

  const normalizeStatus = useCallback((status: any): string | undefined => {
    const normalizedStatus = String(status ?? "").toUpperCase();
    if (!normalizedStatus) return undefined;

    if (normalizedStatus === "ENABLED" || normalizedStatus === "ALLOWED") return "ENABLED";
    if (normalizedStatus === "DISABLED" || normalizedStatus === "DENIED" || normalizedStatus === "BLOCKED") return "DISABLED";
    if (normalizedStatus === "DYNAMIC" || normalizedStatus === "TIMED") return "DYNAMIC";
    if (normalizedStatus === "INHERITED") return "INHERITED";

    return normalizedStatus;
  }, []);

  const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";

  const fetchPublishers = async () => {
    setLoadingPublishers(true);
    try {
      let response: Publishers[] = [];

      if (selectedUser === "system")
        response = await getPublishersWithProfiles(os, profileId);
      else
        response = await getPublishers(tenantName, device?.id);

      if (response && Array.isArray(response)) {
        const uniquePublishers = response.filter(
          (pub, index, self) =>
            index === self.findIndex(
              (p) => p?.id === pub?.id || p?.friendlyName === pub?.friendlyName
            )
        );
        const unsignedApp = uniquePublishers.find(
          (pub) => pub?.friendlyName === "Unsigned Apps"
        );

        const remainingPublishers = uniquePublishers.filter(
          (pub) => pub?.friendlyName !== "Unsigned Apps"
        );


        const customEntries: Publishers[] = [
          {
            id: -1,
            friendlyName: "All Apps",
            issuer: "",
            notAfter: "",
            notBefore: "",
            subject: "",
            thumbprint: "",
            containsApps: true,
            categories: [],
            version: null,
            pspath: null,
          },
        ];
        const finalPublishers = unsignedApp
          ? [...customEntries, unsignedApp, ...remainingPublishers]
          : [...customEntries, ...remainingPublishers];
        setPublishers(finalPublishers);
      } else {
        setPublishers([]);
      }
    } catch (error) {
      console.error("Error fetching publishers:", error);
      setPublishers([]);
    } finally {
      setLoadingPublishers(false);
    }
  };

  const fetchSearchedApps = async () => {
    let response = await getAllAppsWithProfileSearch(profileId, appSearch, currentPage, PAGE_SIZE, normalizedPrivilegeFilter);

    if (response && Array.isArray(response)) {
      const uniqueApps = response.filter(
        (app, index, self) =>
          index === self.findIndex(
            (a) => getAppId(a) === getAppId(app)
          )
      );
      setApps(uniqueApps);

    }
  }

  const fetchTotalAppsCountDetails = async (searchQuery = appSearch) => {
    let response = await getTotalAppsCount(profileId, searchQuery, selectedPublisher || -1, os, normalizedPrivilegeFilter);
    setTotalAppsCountDetails(response)
  }

  const fetchAppsCountDetails = async (searchQuery = appSearch) => {
    let response = await getAllAppsCount(device?.id, selectedUser || "", searchQuery, normalizedPrivilegeFilter);
    setTotalAppsCountDetails(response);
  }

  const fetchAppsCountDetailsWithPublisher = async (searchQuery = appSearch) => {
    let response = await getAppsForPublishersCount(tenantName, selectedUser || "", selectedPublisher || -1, searchQuery, normalizedPrivilegeFilter);
    setTotalAppsCountDetails(response);
  }



  // Fetch apps
  const fetchApps = async (isFirst = true, searchQuery = appSearch, page = currentPage) => {
    setLoadingApps(isFirst);
    let response: ResourceAPP[] = [];
    try {
      if (selectedPublisher === -1 && selectedUser) {
        if (selectedUser === "system")
          response = await getAllAppsWithProfile(profileId, page, PAGE_SIZE, normalizedPrivilegeFilter, searchQuery);
        else
          response = await getAllApps(device?.id, selectedUser || "", searchQuery, page, PAGE_SIZE, normalizedPrivilegeFilter);
      } else if (selectedPublisher !== null && selectedUser) {
        if (selectedUser === "system")
          response = await getAppsForPublishersWithProfile(profileId, os, tenantName, selectedPublisher, page, PAGE_SIZE, normalizedPrivilegeFilter, searchQuery);
        else
          response = await getAppsForPublishers(tenantName, selectedUser || "", selectedPublisher, normalizedPrivilegeFilter, searchQuery);
      }

      if (response && Array.isArray(response)) {
        const uniqueApps = response.filter(
          (app, index, self) =>
            index === self.findIndex(
              (a) => getAppId(a) === getAppId(app)
            )
        );
        setApps(uniqueApps);
      } else {
        setApps([]);
      }
    } catch (error) {
      console.error("Error fetching apps:", error);
      setApps([]);
    } finally {
      setLoadingApps(false);
    }
  };

  useEffect(() => {
    if (publishers.length > 0) {
      const unsignedApp = publishers.find(
        (pub) => pub?.friendlyName === "Unsigned Apps"
      );

      if (unsignedApp && fromDetails) {
        setSelectedPublisher(unsignedApp.id);
      } else {
        setSelectedPublisher(-1);
      }
    }

  }, [publishers])



  useEffect(() => {
    fetchPublishers();
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser && selectedPublisher !== null &&
      selectedPublisher !== undefined &&
      publishers.length > 0
    ) {
      fetchApps();

    }

  }, [selectedPublisher, selectedUser, publishers]);

  useEffect(() => {
    if (selectedUser && selectedPublisher !== null &&
      selectedPublisher !== undefined &&
      publishers.length > 0
    ) {
      fetchAppCount(appSearch);
    }
  }, [selectedPublisher, selectedUser, publishers, fromDetails, os, privilegeFilter, appSearch]);

  const fetchAppCount = (searchQuery = appSearch) => {
    if (fromDetails) fetchTotalAppsCountDetails(searchQuery)
    else if (selectedPublisher !== -1) {
      fetchAppsCountDetailsWithPublisher(searchQuery);
    } else {
      fetchAppsCountDetails(searchQuery);
    }
  }

  useEffect(() => {
    if (selectedUser && selectedPublisher !== null && selectedPublisher !== undefined) {
      fetchApps();
    }
  }, [currentPage, privilegeFilter])

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedUser, selectedPublisher, fromDetails, os]);


  const handleStatusChange = async (
    appId: number,
    computerUserPublisher: any,
    newStatus: string
  ) => {
    if (!selectedUser || selectedPublisher === null || selectedPublisher === undefined) return;

    const normalizedStatus = newStatus === "BLOCKED" ? "DISABLED" : newStatus;

    const resolvedAppId = String(
      appId ?? computerUserPublisher?.appId ?? computerUserPublisher?.id ?? ""
    );
    if (!resolvedAppId) return;
    // Update local UI state
    setAppStatuses((prev) => ({
      ...prev,
      [selectedUser]: {
        ...(prev[selectedUser] ?? {}),
        [resolvedAppId]: normalizedStatus,
      },
    }));

    // Resource mapping
    const resourceMapping: Record<string, string> = {
      ENABLED: "ALLOWED",
      DISABLED: "BLOCKED",
      DYNAMIC: "DYNAMIC",
      INHERITED: "INHERITED",
    };

    // Update UI state of apps
    const updatedApplications = apps.map((app) =>
      String(getAppId(app)) === resolvedAppId
        ? {
          ...app,
          [getAppPayloadKey(app)]: {
            ...getAppData(app),
            groupStatus: normalizedStatus,
            trustLevel: resourceMapping[normalizedStatus] || normalizedStatus,
            profileResource: resourceMapping[normalizedStatus] || "",
          },
        }
        : app
    );

    setApps(updatedApplications);
    setSelectedApp((prevSelectedApp) => {
      if (!prevSelectedApp) return prevSelectedApp;
      if (String(getAppId(prevSelectedApp)) !== resolvedAppId) return prevSelectedApp;

      return {
        ...prevSelectedApp,
        [getAppPayloadKey(prevSelectedApp)]: {
          ...getAppData(prevSelectedApp),
          groupStatus: normalizedStatus,
          trustLevel: resourceMapping[normalizedStatus] || normalizedStatus,
          profileResource: resourceMapping[normalizedStatus] || "",
        },
      };
    });

    try {
      const timeInSeconds = (device?.defaultTime || 0) * 60;
      if (selectedUser === "system") {
        const profilePayload = {
          ...computerUserPublisher,
          tenantName: tenantName,
          appId: computerUserPublisher?.appId ?? computerUserPublisher?.id,
        };
        await updateTrustLevelForProfile(profilePayload, normalizedStatus, email, role, profileId);
      }
      else
        await updateTrustLevel(computerUserPublisher, normalizedStatus, tenantName, selectedUser, email, role, timeInSeconds);
    } catch (error) {
      console.error("Failed to update trust level:", error);
    }
    await fetchApps(false);
  };

  const getAppStatus = (app: ResourceAPP) => {
    const appData = getAppData(app);

    if (!appData) return undefined;

    if (selectedUser === "system") {
      const profileStatus = normalizeStatus(appData.profileResource);
      const trustStatus = normalizeStatus(appData.trustLevel);

      if (profileStatus === "INHERITED" && trustStatus) {
        return trustStatus;
      }

      return profileStatus || trustStatus;
    }

    const derivedStatus =
      normalizeStatus(appData.groupStatus) ||
      normalizeStatus(appData.trustLevel) ||
      normalizeStatus(appData.profileResource);

    if (!selectedUser) {
      return derivedStatus;
    }

    const appStatusKey = String(appData.appId ?? appData.id ?? "");

    return (
      (appStatusKey ? appStatuses?.[selectedUser]?.[appStatusKey] : undefined) ||
      derivedStatus
    );
  };

  const getMappedStatusLabel = (app: ResourceAPP) => {
    const status = getAppStatus(app) || "";
    const inheritedStatus = selectedUser === "system"
      ? ""
      : (getInheritedEndpointResourceStatusValue(app, getData) || "");
    return labelMapping(status, inheritedStatus);
  }

  const getStatus = (app: ResourceAPP) => {
    return app.isDenied ? "DENIED" : getMappedStatusLabel(app)
  }

  const getSelectStatusValue = (app: ResourceAPP | null | undefined) => {
    const status = getAppStatus(app as ResourceAPP);
    if (!status) return undefined;

    const normalizedStatus = normalizeStatus(status) || String(status).toUpperCase();
    if (normalizedStatus === "INHERITED" && selectedUser === "system") {
      const appData = getAppData(app as ResourceAPP);
      const hasCategoryName = Boolean(String(appData?.categoryName ?? "").trim());
      if (hasCategoryName) return "INHERITED";
      const inheritedStatus = normalizeStatus(
        appData?.trustLevel || appData?.profileResource || ""
      );

      if (inheritedStatus === "DISABLED" || inheritedStatus === "DYNAMIC" || inheritedStatus === "ENABLED") return inheritedStatus;
      return "ENABLED";
    }
    return normalizedStatus;
  }



  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(2)} ${sizes[i]}`;
  };

  const filteredPublishers = publishers.filter((publisher) =>
    publisher.friendlyName.toLowerCase().includes(publisherSearch.toLowerCase())
  );

  const filteredUsers = useMemo(() => {
    return users?.filter((user) =>
      user.cn.toLowerCase().includes(userSearch.toLowerCase())
    );
  }, [userSearch, users]);

  const filteredApps = useMemo(() => {
    const filterMap: Record<string, string[]> = {
      ALL: ["ALLOWED", "DENIED", "TIMED", "INHERITED", "BLOCKED"],
      ALLOWED: ["ALLOWED"],
      DENIED: ["DENIED", "BLOCKED"],
      TIMED: ["TIMED"],
    };


    // First, filter by privilege and search
    const filtered = apps.filter((app) => {
      const appData = getAppData(app);
      if (!appData) return false;
      const status = app.isDenied ? "DENIED" : getMappedStatusLabel(app);
      const matchesPrivilege = filterMap[privilegeFilter]?.includes(status) ?? true;
      const appName = getAppNameValue(app).toLowerCase();
      const appPath = getAppPathValue(app).toLowerCase();
      const searchText = appSearch.toLowerCase().trim();
      const matchesSearch =
        !searchText || appName.includes(searchText) || appPath.includes(searchText);

      return matchesPrivilege && matchesSearch;
    });



    // Then, make unique by path
    const seenPaths = new Set<string>();
    return filtered.filter(app => {
      const pathValue = getAppPathValue(app) || getAppNameValue(app);
      if (!pathValue) return false;
      if (seenPaths.has(pathValue)) return false;
      seenPaths.add(pathValue);
      return true;
    });
  }, [apps, appSearch, privilegeFilter, appStatuses, selectedUser, fromDetails, getAppData, getAppNameValue, getAppPathValue]);
  const handlePrivilegeFilterChange = (value: string) => {
    setSelectedApp(null);
    setCurrentPage(0);
    setPrivilegeFilter(value);
  }

  const handleRefreshApps = async () => {
    await fetchApps();
    await fetchAppCount(appSearch);
  };

  const handleAppsSearchClick = async () => {
    const searchValue = appSearchInput.trim();
    setAppSearch(searchValue);
    setCurrentPage(0);
    fetchAppCount(searchValue);
    await fetchApps(true, searchValue, 0);
  };

  const supportsServerPagination = fromDetails || selectedPublisher === -1 || selectedUser === "system";
  const hasNextPage = supportsServerPagination && (currentPage + 1) * PAGE_SIZE < (totalAppsCountDetails || 0);

  const handlePreviousPage = () => {
    if (loadingApps || currentPage === 0) return;
    setSelectedApp(null);
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    if (loadingApps || !hasNextPage) return;
    setSelectedApp(null);
    setCurrentPage((prev) => prev + 1);
  };

  const selectedPublisherData = publishers.find((p) => p.id === selectedPublisher);
  const selectedAppData = selectedApp ? getAppData(selectedApp) : null;
  const canShowInheritedOptionForSystem =
    selectedUser === "system" &&
    Boolean(String(selectedAppData?.categoryName ?? "").trim());
  const inheritedOptionHint = (() => {
    if (selectedUser !== "system") {
      const value = getInheritedEndpointResourceStatusValue(selectedApp, getData);
      return value || "";
    }

    const hasCategoryName = Boolean(String(selectedAppData?.categoryName ?? "").trim());
    if (!hasCategoryName) return "";

    const value = getInheritedEndpointResourceStatusValue(selectedApp, getData);
    if (!value) return "";
    const normalizedValue = value.toLowerCase();
    return normalizedValue.includes("undefined") || normalizedValue.includes("null") ? "" : value;
  })();



  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Users List */}
      {selectedUser !== "system" && (
        <div className="flex flex-col w-48 p-4 bg-white border-r dark:bg-background">
          <h3 className="mb-4 text-sm font-medium">Users List</h3>
          <Input
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="mb-3 text-sm"
          />
          <div className="flex-1 space-y-3 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="py-6 text-xs text-center text-muted-foreground">
                No users found
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${String(selectedUser) === String(user.id)
                    ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    : "hover-elevate text-muted-foreground"
                    }`}
                  onClick={() => {
                    onUserSelect(user.id);
                    setSelectedPublisher(null);
                    setSelectedApp(null);
                  }}
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-sm font-medium">{user.cn}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Publishers List */}
      <div className="flex flex-col w-64 p-4 bg-white border-r dark:bg-background">
        <h3 className="mb-4 text-sm font-medium">Publisher List</h3>
        <Input
          placeholder="Search publishers..."
          value={publisherSearch}
          onChange={(e) => setPublisherSearch(e.target.value)}
          className="mb-3 text-sm"
          disabled={!selectedUser}
        />
        <div className="flex-1 space-y-2 overflow-y-auto">
          {loadingPublishers ? (
            <>
              <Skeleton className="w-3/4 h-4 mb-2" />
              <Skeleton className="w-2/3 h-4 mb-2" />
              <Skeleton className="w-1/2 h-4 mb-2" />
            </>
          ) : filteredPublishers.length === 0 ? (
            <div className="py-6 text-xs text-center text-muted-foreground">
              No publishers found
            </div>
          ) : (
            filteredPublishers.map((publisher) => (
              <div
                key={publisher.id}
                className={`p-3 rounded-md cursor-pointer transition-colors border ${selectedPublisher === publisher.id
                  ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                  : "hover-elevate border-border"
                  }`}
                onClick={() => {
                  setSelectedPublisher(publisher.id);
                  setAppSearchInput("");
                  setAppSearch("");
                }}
              >
                <span className="text-sm font-medium">{publisher.friendlyName}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Apps List */}
      <div className="flex flex-col flex-1 bg-white border-r dark:bg-background">
        {selectedPublisher && selectedUser && (
          <div className="flex flex-col flex-1 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">
                Apps from {selectedPublisherData?.friendlyName} count: {Array.isArray(totalAppsCountDetails) ? totalAppsCountDetails.length : totalAppsCountDetails || 0}
              </h3>
              {!fromDetails && (<Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleRefreshApps}
                disabled={loadingApps}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingApps ? "animate-spin" : ""}`} />
                Refresh
              </Button>)}

            </div>

            <div className="relative mb-4">
              <Input
                placeholder="Search apps..."
                value={appSearchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setCurrentPage(0);
                  setAppSearchInput(value);
                  if (!value.trim()) {
                    setAppSearch("");
                    fetchApps(true, "", 0);
                    fetchAppCount("");
                  }
                }}
                className="h-10 pr-12 text-sm"
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  type="button"
                  onClick={handleAppsSearchClick}
                  disabled={loadingApps}
                  className="pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 mb-2">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-muted-foreground">Privilege</span>
                <RadioGroup value={privilegeFilter} onValueChange={handlePrivilegeFilterChange} className="flex gap-4">
                  {["ALL", "ALLOWED", "DENIED", "TIMED"].map((v) => (
                    <div key={v} className="flex items-center space-x-2">
                      <RadioGroupItem value={v} id={`filter-${v.toLowerCase()}`} />
                      <Label htmlFor={`filter-${v.toLowerCase()}`} className="text-sm">{v}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handlePreviousPage}
                  disabled={loadingApps || currentPage === 0}
                >
                  {"<"}
                </Button>
                <span className="text-xs text-muted-foreground">Page {currentPage + 1}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleNextPage}
                  disabled={loadingApps || !hasNextPage}
                >
                  {">"}
                </Button>
              </div>
            </div>

            <div className="flex flex-col flex-1 border rounded-md">
              <div className="grid grid-cols-12 gap-4 p-3 text-xs font-medium border-b bg-muted/50 text-muted-foreground">
                <div className="col-span-8">Application</div>
                <div className="col-span-4">Status</div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loadingApps ? (
                  <>
                    <Skeleton className="w-full h-6 mb-2" />
                    <Skeleton className="w-5/6 h-6 mb-2" />
                    <Skeleton className="w-2/3 h-6 mb-2" />
                  </>
                ) : filteredApps?.length === 0 ? (
                  <div className="py-6 text-xs text-center text-muted-foreground">No applications found</div>
                ) : (
                  filteredApps?.map((app) => {
                    const membership = getAppData(app);
                    if (!membership) return null;
                    const selectedMembership = selectedApp ? getAppData(selectedApp) : null;
                    const appName = getAppNameValue(app);
                    const appPath = getAppPathValue(app);
                    const appId = getAppId(app);
                    return (
                      <div
                        key={appId || `${appPath || appName}-${membership.publisherId || "na"}`}
                        className={`grid grid-cols-12 gap-4 p-3 border-b last:border-b-0 hover-elevate cursor-pointer ${selectedMembership?.appId === membership.appId
                          ? "bg-blue-50 dark:bg-blue-900/20"
                          : ""
                          }`}
                        onClick={() => setSelectedApp(app)}
                      >
                        <div className="flex items-start col-span-8 gap-3">
                          <span className="flex-shrink-0 w-5 h-5 mt-1 text-muted-foreground">📦</span>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-sm font-medium text-foreground">{appName}</span>
                            <span className="font-mono text-xs truncate text-muted-foreground cursor-help" title={appPath}>
                              {appPath}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center col-span-4 gap-2">

                          <Badge
                            variant={
                              getStatus(app) === "ALLOWED"
                                ? "default"
                                : getStatus(app) === "DENIED"
                                  ? "destructive"
                                  : getStatus(app) === "TIMED"
                                    ? "secondary"
                                    : "default"
                            }
                            className="text-xs min-w-[60px] text-center"
                          >


                            {app.isDenied ? "DENIED" : getMappedStatusLabel(app)}
                          </Badge>

                          {getMappedStatusLabel(app) === "TIMED" && !fromDetails && selectedUser !== "system" && (
                            <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-medium min-w-[50px]">
                              <Timer className="w-3 h-3" />
                              <span>{getTimerDisplay(membership.remainingTime)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* App Properties Panel */}
      {selectedApp && selectedPublisher && selectedUser ? (
        <div className="p-4 overflow-y-auto bg-white border-l w-80 dark:bg-background">
          <h3 className="mb-2 text-lg font-semibold">
            {selectedAppData?.appName}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-xs font-medium tracking-wide uppercase text-muted-foreground">
                Access Control
              </label>
              <Select
                value={getSelectStatusValue(selectedApp)}
                onValueChange={(value) =>
                  handleStatusChange(
                    selectedAppData?.appId,
                    selectedAppData,
                    value
                  )
                }
              >
                <SelectTrigger className="h-auto min-h-8 text-xs w-55 border-blue-200 whitespace-normal break-words dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">

                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENABLED">ALLOWED</SelectItem>
                  <SelectItem value="DISABLED">DENIED</SelectItem>
                  <SelectItem value="DYNAMIC">TIMED</SelectItem>
                  {(selectedUser !== "system" || canShowInheritedOptionForSystem) && (
                    <SelectItem value="INHERITED">
                      INHERITED {inheritedOptionHint}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-4 space-y-3 border-t">
              <div>
                <label className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  Filename
                </label>
                <div className="mt-1 text-sm font-medium">
                  {(selectedAppData?.appName || selectedAppData?.name || "")
                    .toLowerCase()
                    .replace(/\s+/g, "")}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  File Path
                </label>
                <div className="p-2 mt-1 font-mono text-xs break-all rounded-md bg-muted">
                  {selectedAppData?.path || selectedAppData?.appPath}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  File Size
                </label>
                <div className="mt-1 text-sm font-medium">
                  {formatFileSize(Number(selectedAppData?.size))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  SHA256
                </label>
                <div className="p-2 mt-1 font-mono text-xs break-all rounded-md bg-muted">
                  {selectedAppData?.fingerprintSha256}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
